import { useState, useEffect, useCallback, useRef } from 'react';
import { getErrorMessage } from '../services/apiService';

/**
 * Custom hook for API data fetching with loading, error states, and retry logic
 * 
 * @param {Function} apiFunction - The API function to call
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Whether to call immediately on mount (default: true)
 * @param {Array} options.deps - Dependencies array for refetching
 * @param {Function} options.onSuccess - Callback on successful fetch
 * @param {Function} options.onError - Callback on error
 * @param {any} options.initialData - Initial data value
 * @param {boolean} options.enabled - Whether the query is enabled (default: true)
 * 
 * @returns {Object} { data, loading, error, refetch, reset }
 */
export function useApi(apiFunction, options = {}) {
  const {
    immediate = true,
    deps = [],
    onSuccess,
    onError,
    initialData = null,
    enabled = true,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate && enabled);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  // Execute the API call
  const execute = useCallback(async (...args) => {
    const fetchId = ++fetchIdRef.current;

    try {
      setLoading(true);
      setError(null);

      const result = await apiFunction(...args);

      // Only update state if this is the latest request and component is mounted
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        setData(result);
        onSuccess?.(result);
        return result;
      }
    } catch (err) {
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        onError?.(err);
        throw err;
      }
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunction, onSuccess, onError]);

  // Refetch function
  const refetch = useCallback((...args) => {
    return execute(...args);
  }, [execute]);

  // Reset function
  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
  }, [initialData]);

  // Initial fetch
  useEffect(() => {
    if (immediate && enabled) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    execute,
    reset,
    isIdle: !loading && !error && data === initialData,
    isSuccess: !loading && !error && data !== initialData,
    isError: !loading && !!error,
  };
}

/**
 * Custom hook for mutations (POST, PUT, DELETE)
 * 
 * @param {Function} mutationFunction - The mutation function
 * @param {Object} options - Configuration options
 * 
 * @returns {Object} { mutate, data, loading, error, reset }
 */
export function useMutation(mutationFunction, options = {}) {
  const { onSuccess, onError, onSettled } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const mutate = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const result = await mutationFunction(...args);

      if (mountedRef.current) {
        setData(result);
        onSuccess?.(result, ...args);
        return result;
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        onError?.(err, ...args);
        throw err;
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        onSettled?.();
      }
    }
  }, [mutationFunction, onSuccess, onError, onSettled]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    mutate,
    mutateAsync: mutate,
    data,
    loading,
    isLoading: loading,
    error,
    isError: !!error,
    isSuccess: !loading && !error && data !== null,
    reset,
  };
}

/**
 * Custom hook for paginated data
 * 
 * @param {Function} apiFunction - API function that accepts { page, limit, ...filters }
 * @param {Object} options - Configuration options
 * 
 * @returns {Object} Pagination state and controls
 */
export function usePaginatedApi(apiFunction, options = {}) {
  const {
    initialPage = 1,
    initialLimit = 25,
    initialFilters = {},
    enabled = true,
  } = options;

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [filters, setFilters] = useState(initialFilters);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = useCallback(async () => {
    const params = { page, limit, ...filters };
    const result = await apiFunction(params);
    
    // Update pagination meta if available
    if (result.meta) {
      setTotalItems(result.meta.total || result.total || 0);
      setTotalPages(result.meta.totalPages || Math.ceil((result.meta.total || 0) / limit));
    }
    
    return result;
  }, [apiFunction, page, limit, filters]);

  const { data, loading, error, refetch, reset } = useApi(fetchData, {
    immediate: enabled,
    deps: [page, limit, JSON.stringify(filters)],
    enabled,
  });

  const goToPage = useCallback((newPage) => {
    setPage(Math.max(1, Math.min(newPage, totalPages || 1)));
  }, [totalPages]);

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      setPage(page - 1);
    }
  }, [page]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page when filters change
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setPage(1);
  }, [initialFilters]);

  return {
    // Data
    data: data?.data || [],
    items: data?.data || [],
    
    // State
    loading,
    error,
    
    // Pagination
    page,
    limit,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
    
    // Filters
    filters,
    
    // Actions
    goToPage,
    nextPage,
    prevPage,
    setLimit,
    updateFilters,
    resetFilters,
    refetch,
    reset,
  };
}

export default useApi;











